import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usersAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import {
  ArrowLeft, UploadCloud, ShieldCheck, Clock, XCircle, CheckCircle2,
  FileSignature, History, AlertTriangle, Loader2,
} from 'lucide-react';

const statusMeta = {
  approved: { label: 'Approved', className: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
  pending: { label: 'Pending Approval', className: 'bg-amber-50 text-amber-700 border-amber-200', icon: Clock },
  rejected: { label: 'Rejected', className: 'bg-red-50 text-red-700 border-red-200', icon: XCircle },
};

export default function MyProfilePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileRef = useRef(null);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imgUrl, setImgUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedPreview, setSelectedPreview] = useState('');
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const res = await usersAPI.mySignatureStamp();
      setData(res.data);
      if (res.data?.signature_stamp?.storage_exists !== false && res.data?.signature_stamp) {
        try {
          const img = await usersAPI.mySignatureStampImage();
          setImgUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(img.data); });
        } catch { setImgUrl(''); }
      } else {
        setImgUrl('');
      }
    } catch {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(f.type)) {
      toast.error('Only PNG or JPG images are allowed'); return;
    }
    if (f.size > 5 * 1024 * 1024) { toast.error('Image too large (max 5 MB)'); return; }
    setSelectedFile(f);
    setSelectedPreview((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(f); });
  };

  const doUpload = async () => {
    if (!selectedFile) { toast.error('Choose an image first'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', selectedFile);
      await usersAPI.uploadMySignatureStamp(fd);
      toast.success('Uploaded — waiting for admin approval');
      setSelectedFile(null);
      setSelectedPreview((prev) => { if (prev) URL.revokeObjectURL(prev); return ''; });
      if (fileRef.current) fileRef.current.value = '';
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const ss = data?.signature_stamp;
  const status = ss?.status;
  const sMeta = statusMeta[status] || null;
  const isCocIssuer = data?.coc_issuer;
  const needsSignature = (user?.role === 'admin' || (user?.role === 'quality_manager' && isCocIssuer));

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-[#0F172A] text-white">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="sm" className="text-white hover:bg-white/10" onClick={() => navigate('/')} data-testid="profile-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-lg font-semibold">My Profile</h1>
            <p className="text-[11px] text-slate-400">Manage your signature &amp; stamp</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6" data-testid="my-profile-page">
        {/* Identity */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Account</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div><span className="text-slate-500 text-xs">Name</span><div className="font-medium" data-testid="profile-name">{user?.name}</div></div>
            <div><span className="text-slate-500 text-xs">Email</span><div className="font-medium">{user?.email}</div></div>
            <div><span className="text-slate-500 text-xs">Role</span><div className="font-medium capitalize">{(user?.role || '').replace('_', ' ')}</div></div>
            <div>
              <span className="text-slate-500 text-xs">COC Issuer</span>
              <div>
                {isCocIssuer
                  ? <Badge className="bg-indigo-50 text-indigo-700 border-indigo-200" data-testid="profile-coc-issuer-badge"><ShieldCheck className="w-3 h-3 mr-1" />Quality COC Issuer</Badge>
                  : <span className="text-slate-400 text-xs">Not a COC issuer</span>}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Prompt when a COC issuer has no approved signature */}
        {needsSignature && status !== 'approved' && (
          <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid="profile-signature-prompt">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">You need an approved signature &amp; stamp to issue Certificates of Conformance.</p>
              <p className="text-xs mt-1">Upload your combined signature+stamp image below. An admin must approve it before it appears on COCs. Upload location: <span className="font-mono">My Profile → Signature &amp; Stamp</span> (this page).</p>
            </div>
          </div>
        )}

        {/* Signature & Stamp */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><FileSignature className="w-4 h-4" /> Signature &amp; Stamp</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {loading ? (
              <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
            ) : (
              <>
                {/* Current */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <div className="text-xs text-slate-500 mb-2">Current</div>
                    {ss ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          {sMeta && <Badge className={sMeta.className} data-testid="signature-status-badge"><sMeta.icon className="w-3 h-3 mr-1" />{sMeta.label}</Badge>}
                        </div>
                        {status === 'rejected' && ss.rejection_reason && (
                          <p className="text-xs text-red-600">Reason: {ss.rejection_reason}</p>
                        )}
                        {status === 'approved' && ss.approved_by_name && (
                          <p className="text-[11px] text-slate-500">Approved by {ss.approved_by_name}</p>
                        )}
                        {imgUrl ? (
                          <img src={imgUrl} alt="signature and stamp" className="max-h-32 border rounded bg-white p-2" data-testid="signature-current-image" />
                        ) : (
                          <div className="text-xs text-slate-400">Preview unavailable</div>
                        )}
                      </div>
                    ) : (
                      <div className="text-sm text-slate-400" data-testid="signature-none">No signature &amp; stamp uploaded yet.</div>
                    )}
                  </div>

                  {/* Upload / replace */}
                  <div className="flex-1">
                    <div className="text-xs text-slate-500 mb-2">{ss ? 'Replace' : 'Upload'} (PNG/JPG, max 5 MB)</div>
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/png,image/jpeg"
                      onChange={onPick}
                      className="block w-full text-sm text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-sm file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
                      data-testid="signature-file-input"
                    />
                    {selectedPreview && (
                      <img src={selectedPreview} alt="selected preview" className="mt-2 max-h-28 border rounded bg-white p-2" data-testid="signature-selected-preview" />
                    )}
                    <Button onClick={doUpload} disabled={!selectedFile || uploading} className="mt-3" size="sm" data-testid="signature-upload-btn">
                      {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <UploadCloud className="w-4 h-4 mr-1" />}
                      {ss ? 'Replace & Submit' : 'Upload & Submit'}
                    </Button>
                    <p className="text-[11px] text-slate-400 mt-2">Any new upload resets status to “Pending” until an admin approves it.</p>
                  </div>
                </div>

                {/* History */}
                {data?.history?.length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-slate-600 flex items-center gap-1 mb-2"><History className="w-3.5 h-3.5" /> Change history</div>
                    <div className="border rounded-md divide-y" data-testid="signature-history">
                      {[...data.history].reverse().map((h, i) => (
                        <div key={i} className="px-3 py-2 text-xs flex items-center justify-between">
                          <span className="capitalize font-medium">{h.action}</span>
                          <span className="text-slate-500">{h.by_name || ''} · {h.at ? new Date(h.at).toLocaleString() : ''}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
